import { Box, Popover, type PopoverProps } from '@mantine/core'
import clsx from 'clsx'
import React from 'react'

import { findReactChildrenByDisplayName } from '@flecto/utils/src/Window/findReactChildrenByDisplayName'

import { Badge, type BadgeProps } from '../Badge/Badge'
import { BadgeEditableClose } from './components/Close'
import { BadgeEditableConfirm } from './components/Confirm'
import { BadgeEditableDropdown } from './components/Dropdown'
import PopoverProvider, { PopoverContext } from './provider'
import classes from './styles.module.css'

type Props = {
  value: string | number | React.ReactNode
  popoverProps?: Omit<PopoverProps, 'children'>
} & BadgeProps

const BadgeEditableComponent = ({
  value,
  popoverProps,
  children,
  ...props
}: React.PropsWithChildren<Props>) => {
  const { opened, openPopover } = React.useContext(PopoverContext)

  const [dropdown, close, confirm] = [
    'BadgeEditable.Dropdown',
    'BadgeEditable.Close',
    'BadgeEditable.Confirm',
  ].map((component) => findReactChildrenByDisplayName(children, component))

  return (
    <Popover
      opened={opened}
      position='top'
      withArrow
      withinPortal
      {...popoverProps}
    >
      <Popover.Target>
        <Box onClick={() => openPopover()}>
          <Badge {...props} className={clsx(classes.badge, props.className)}>
            {value}
          </Badge>
        </Box>
      </Popover.Target>
      <Popover.Dropdown>
        <Box className={classes.container}>
          {dropdown}
          {close}
          {confirm}
        </Box>
      </Popover.Dropdown>
    </Popover>
  )
}

export function BadgeEditable(
  props: React.ComponentProps<typeof BadgeEditableComponent>
) {
  return (
    <PopoverProvider>
      <BadgeEditableComponent {...props} />
    </PopoverProvider>
  )
}

BadgeEditable.Dropdown = BadgeEditableDropdown
BadgeEditable.Close = BadgeEditableClose
BadgeEditable.Confirm = BadgeEditableConfirm
